import { lazy } from 'react';
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from 'react-router-dom';
import { AuthProvider } from '@/lib/auth';
import { I18nProvider } from '@/lib/i18n';
import { ThemeProvider } from '@/lib/theme';
import { ConfirmProvider, ToastProvider } from '@/components/ui';
import { AdminShell, AppShell, DeskShell, HomeRedirect, RequireAuth, RequirePlatform, WorkspaceRoute } from '@/layouts/Shells';
import { Login, Signup, VerifyEmail, ForgotPassword, ResetPassword, MfaChallenge, AcceptInvite } from '@/pages/auth/AuthPages';
import NotFound from '@/pages/NotFound';

const L = (f: () => Promise<{ default: React.ComponentType }>) => { const C = lazy(f); return <C />; };

const router = createBrowserRouter([
  { path: '/login', element: <Login /> },
  { path: '/signup', element: <Signup /> },
  { path: '/verify-email', element: <VerifyEmail /> },
  { path: '/forgot-password', element: <ForgotPassword /> },
  { path: '/reset-password', element: <ResetPassword /> },
  { path: '/mfa', element: <MfaChallenge /> },
  { path: '/invite/:token', element: <AcceptInvite /> },
  { path: '/chat/:publicKey', element: L(() => import('@/pages/public/HostedChat')) },
  {
    element: <RequireAuth><Outlet /></RequireAuth>,
    children: [
      { path: '/', element: <HomeRedirect /> },
      { path: '/new-workspace', element: L(() => import('@/pages/NewWorkspace')) },
      { path: '/account', element: L(() => import('@/pages/Account')) },
      {
        path: '/admin',
        element: <RequirePlatform><AdminShell /></RequirePlatform>,
        children: [
          { index: true, element: L(() => import('@/pages/admin/Overview')) },
          { path: 'workspaces', element: L(() => import('@/pages/admin/Workspaces')) },
          { path: 'workspaces/:id', element: L(() => import('@/pages/admin/WorkspaceDetail')) },
          { path: 'members', element: L(() => import('@/pages/admin/Members')) },
          { path: 'bots', element: L(() => import('@/pages/admin/Bots')) },
          { path: 'plans', element: L(() => import('@/pages/admin/Plans')) },
          { path: 'subscriptions', element: L(() => import('@/pages/admin/Subscriptions')) },
          { path: 'invoices', element: L(() => import('@/pages/admin/Invoices')) },
          { path: 'usage', element: L(() => import('@/pages/admin/Usage')) },
          { path: 'ai-costs', element: L(() => import('@/pages/admin/AiCosts')) },
          { path: 'operations', element: L(() => import('@/pages/admin/Operations')) },
          { path: 'webhook-logs', element: L(() => import('@/pages/admin/WebhookLogs')) },
          { path: 'security', element: L(() => import('@/pages/admin/Security')) },
          { path: 'flags', element: L(() => import('@/pages/admin/Flags')) },
          { path: 'uat', element: L(() => import('@/pages/admin/UatRunner')) },
          { path: 'settings', element: L(() => import('@/pages/admin/PlatformSettings')) },
        ],
      },
      {
        path: '/w/:slug',
        element: <WorkspaceRoute />,
        children: [
          {
            path: 'desk',
            element: <DeskShell />,
            children: [
              { index: true, element: L(() => import('@/pages/desk/DeskInbox')) },
              { path: 'c/:conversationId', element: L(() => import('@/pages/desk/DeskInbox')) },
              { path: 'queue', element: L(() => import('@/pages/desk/DeskInbox')) },
              { path: 'queue/:conversationId', element: L(() => import('@/pages/desk/DeskInbox')) },
              { path: 'customers', element: L(() => import('@/pages/desk/Customers')) },
              { path: 'customers/:contactId', element: L(() => import('@/pages/desk/Customers')) },
              { path: 'tickets', element: L(() => import('@/pages/desk/DeskTickets')) },
              { path: 'tickets/:ticketId', element: L(() => import('@/pages/desk/DeskTickets')) },
              { path: 'replies', element: L(() => import('@/pages/desk/Replies')) },
              { path: 'stats', element: L(() => import('@/pages/desk/Stats')) },
            ],
          },
          {
            element: <AppShell />,
            children: [
              { index: true, element: L(() => import('@/pages/app/Home')) },
              { path: 'onboarding', element: L(() => import('@/pages/app/Onboarding')) },
              { path: 'agents', element: L(() => import('@/pages/app/Agents')) },
              { path: 'agents/:agentId', element: L(() => import('@/pages/app/AgentDetail')) },
              { path: 'knowledge', element: L(() => import('@/pages/app/Knowledge')) },
              { path: 'test-lab', element: L(() => import('@/pages/app/TestLab')) },
              { path: 'flows', element: L(() => import('@/pages/app/Flows')) },
              { path: 'flows/:flowId', element: L(() => import('@/pages/app/FlowEditor')) },
              { path: 'inbox', element: L(() => import('@/pages/app/Inbox')) },
              { path: 'inbox/:conversationId', element: L(() => import('@/pages/app/Inbox')) },
              { path: 'tickets', element: L(() => import('@/pages/app/Tickets')) },
              { path: 'tickets/:ticketId', element: L(() => import('@/pages/app/TicketDetail')) },
              { path: 'contacts', element: L(() => import('@/pages/app/Contacts')) },
              { path: 'contacts/:contactId', element: L(() => import('@/pages/app/ContactDetail')) },
              { path: 'leads', element: L(() => import('@/pages/app/Leads')) },
              { path: 'workflows', element: L(() => import('@/pages/app/Workflows')) },
              { path: 'workflows/:workflowId', element: L(() => import('@/pages/app/WorkflowEditor')) },
              { path: 'campaigns', element: L(() => import('@/pages/app/Campaigns')) },
              { path: 'campaigns/:campaignId', element: L(() => import('@/pages/app/CampaignDetail')) },
              { path: 'channels', element: L(() => import('@/pages/app/Channels')) },
              { path: 'integrations', element: L(() => import('@/pages/app/Integrations')) },
              { path: 'analytics', element: L(() => import('@/pages/app/Analytics')) },
              { path: 'team', element: L(() => import('@/pages/app/Team')) },
              { path: 'billing', element: L(() => import('@/pages/app/Billing')) },
              { path: 'developers', element: L(() => import('@/pages/app/Developers')) },
              { path: 'branding', element: L(() => import('@/pages/app/Branding')) },
              { path: 'settings', element: L(() => import('@/pages/app/Settings')) },
              { path: '*', element: <NotFound /> },
            ],
          },
        ],
      },
    ],
  },
  { path: '/app', element: <Navigate to="/" replace /> },
  { path: '*', element: <NotFound /> },
]);

export default function App() {
  return (
    <ThemeProvider>
      <I18nProvider>
        <ToastProvider>
          <ConfirmProvider>
            <AuthProvider>
              <RouterProvider router={router} />
            </AuthProvider>
          </ConfirmProvider>
        </ToastProvider>
      </I18nProvider>
    </ThemeProvider>
  );
}
